import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AuthenticatedCleanup, BookNextSessionRequest, CoachDashboardResponse, CoachHttpService, CreatePrepQuestionsRequest, JourneyDetailsResponse, ListJourneysCommand, ListJourneysResponse, SubmitPrepQuestionsRequest } from "./coach-http.service";

@Injectable({ providedIn: 'root' })

export class CoachService {
  private currentJourneyStatusSubject = new BehaviorSubject<boolean | null>(null);
  currentJourneyStatus$ = this.currentJourneyStatusSubject.asObservable();

  constructor(private _coachHttpService: CoachHttpService) { }

  setupAuthGuard(): Promise<AuthenticatedCleanup> {
    return this._coachHttpService.setupAuthGuard()
  }

  setJourneyStatus(isCompleted: boolean | null) {
    this.currentJourneyStatusSubject.next(isCompleted);
  }

  //login service
  coachLogin(login_info) {

    return this._coachHttpService.postCoachLogin(login_info)
      .pipe(map(
        res => res
      ));
  }

  //coach sendPasswordToEmail
  sendPasswordToEmail(email_info) {
    return this._coachHttpService.sendPasswordToEmail(email_info)
      .pipe(map(
        res => res
      ));
  }

  //verify reset password token
  verifyPasswordResetToken(token_info) {
    return this._coachHttpService.verifyPasswordResetToken(token_info)
      .pipe(map(
        res => res
      ));
  }

  //verify reset password token
  verifyPasswordSetToken(token_info) {
    return this._coachHttpService.verifyPasswordSetToken(token_info)
      .pipe(map(
        res => res
      ));
  }

  //verify Time slot confirmation token
  verifyTimeSlotConfirmToken(token_info) {
    return this._coachHttpService.verifyTimeSlotConfirmToken(token_info)
      .pipe(map(
        res => res
      ));
  }

  //verify Time slot confirmation token
  verifyReschedulingTimeSlotConfirmToken(token_info) {
    return this._coachHttpService.verifyReschedulingTimeSlotConfirmToken(token_info)
      .pipe(map(
        res => res
      ));
  }

  //reset the password
  resetPassword(password_details) {
    return this._coachHttpService.resetPassword(password_details)
      .pipe(map(
        res => res
      ));
  }

  //set the password
  setPassword(password_details) {
    return this._coachHttpService.setPassword(password_details)
      .pipe(map(
        res => res
      ));
  }

  //confirmed time slot
  confirmedTimeSlot(timeslot_details) {
    return this._coachHttpService.confirmedTimeSlot(timeslot_details)
      .pipe(map(
        res => res
      ));
  }

  //confim rescheduling time
  confirmedReschedulingTimeSlot(timeslot_details) {
    return this._coachHttpService.confirmedReschedulingTimeSlot(timeslot_details)
      .pipe(map(
        res => res
      ));
  }

  getCoachJourneys(cmd: ListJourneysCommand): Observable<ListJourneysResponse> {
    return this._coachHttpService.getCoachJourneys(cmd)
      .pipe(map(
        res => res
      ));
  }

  //get coach journey file list to display
  fetchCoachJourneyFilesList(journey_id) {
    return this._coachHttpService.fetchCoachJourneyFilesList(journey_id)
      .pipe(map(
        res => res
      ));
  }

  //post coach journey file document
  coachUploadJourneyFiles(coach_journey_details) {
    return this._coachHttpService.coachUploadJourneyFiles(coach_journey_details)
      .pipe(map(
        res => res
      ));
  }

  //fetch coachee details
  fetchCoachProfileDetails(coach_id) {
    return this._coachHttpService.fetchCoachProfileDetails(coach_id)
      .pipe(map(
        res => res
      ));
  }

  // coachee password update
  coachPasswordUpdate(profile_details) {
    return this._coachHttpService.coachPasswordUpdate(profile_details)
      .pipe(map(
        res => res
      ));
  }

  getCoachDashboard() {
    return this._coachHttpService.getCoachDashboard()
      .pipe(map((res: CoachDashboardResponse) => res))
  }

  submitPrepQuestions(sessionId: number, cmd: SubmitPrepQuestionsRequest) {
    return this._coachHttpService.submitPrepQuestions(sessionId, cmd)
  }

  createNextSessionPrepQuestions(journeyId: number, cmd: CreatePrepQuestionsRequest) {
    return this._coachHttpService.createNextSessionPrepQuestions(journeyId, cmd)
      .pipe(map(res => res))
  }

  markPrepQuestionAnswersSeen(sessionId: number) {
    return this._coachHttpService.markPrepQuestionAnswersSeen(sessionId)
      .pipe(map(res => res))
  }

  markPrepQuestionAnswersNotSeen(sessionId: number) {
    return this._coachHttpService.markPrepQuestionAnswersNotSeen(sessionId)
    .pipe(map(res => res))
  }

  // book subsequent session
  bookNextSession(journeyId: number, cmd: BookNextSessionRequest) {
    return this._coachHttpService.bookNextSession(journeyId, cmd)
      .pipe(map(
        res => res
      ));
  }

  //end Journey
  endJourney(session_id) {
    return this._coachHttpService.endJourney(session_id)
      .pipe(map(
        res => res
      ));
  }

  coachJoinSession(journeyId, sessionId) {
    return this._coachHttpService.coachJoinSession(journeyId, sessionId)
      .pipe(map(
        res => res
      ));
  }

  //fetch preparation question and answers added by coachee
  fetchPrepQuestAndAnswers(session_id) {
    return this._coachHttpService.fetchPrepQuestAndAnswers(session_id)
      .pipe(map(
        res => res
      ));
  }

  //fetch session ended details
  fetchSessionEndedDetails(journey_id) {
    return this._coachHttpService.fetchSessionEndedDetails(journey_id)
      .pipe(map(
        res => res
      ));
  }

  //get qualitrics questions
  getCoachQualitricsQuestions(coach_id_and_language) {
    return this._coachHttpService.getCoachQualitricsQuestions(coach_id_and_language)
      .pipe(map(
        res => res
      ));
  }

  //store qualitrics answers and questions of coach
  storeCoachQualitricsQuestionsAnswers(question_answers) {
    return this._coachHttpService.storeCoachQualitricsQuestionsAnswers(question_answers)
      .pipe(map(
        res => res
      ));
  }

  //rescheduling session
  reschedulingSession(rescheduling_info) {
    return this._coachHttpService.reschedulingSession(rescheduling_info)
      .pipe(map(
        res => res
      ));
  }

  //get coachee action items
  getActionItemForCoachee(session_id) {
    return this._coachHttpService.getActionItemForCoachee(session_id)
      .pipe(map(
        res => res
      ));
  }
  //get coachee action items
  getKeyTakeawaysForCoachee(journey_info) {
    return this._coachHttpService.getKeyTakeawaysForCoachee(journey_info)
      .pipe(map(
        res => res
      ));
  }

  //save coaching history action items
  saveActionItemsEditedInCoachingHistory(journey_details) {
    return this._coachHttpService.saveActionItemsEditedInCoachingHistory(journey_details)
      .pipe(map(
        res => res
      ));
  }
  //save coaching history action items
  saveKeytakewaysEditedInCoachingHistory(journey_details) {
    return this._coachHttpService.saveKeytakewaysEditedInCoachingHistory(journey_details)
      .pipe(map(
        res => res
      ));
  }

  saveNotes(sessionId, notes) {
    return this._coachHttpService.saveCoachSessionNotes(sessionId, notes)
      .pipe(map(
        res => res
      ));
  }

  deleteFile(fileid) {
    return this._coachHttpService.Delete_coach_file(fileid)
      .pipe(map(
        res => res
      ));
  }

  markDocumentAsSeen(journeyId, fileId) {
    return this._coachHttpService.markDocumentAsSeen(journeyId, fileId)
      .pipe(map(
        res => res
      ));
  }

  getAnalyticsConfiguration() {
    return this._coachHttpService.getAnalyticsConfiguration()
      .pipe(map(
        res => res
      ));
  }

  getProgramBriefingUrl(journeyId: number): Observable<{ url: string }> {
    return this._coachHttpService.getProgramBriefingUrl(journeyId).pipe(
      map(res => res as { url: string })
    );
  }

  getUserBriefingUrl(journeyId: number): Observable<{ url: string }> {
    return this._coachHttpService.getUserBriefingUrl(journeyId).pipe(
      map(res => res as { url: string })
    );
  }

  getSessionById(session_id: string|number, includePrepQuestions = false) {
    return this._coachHttpService
      .getSessionById(session_id, includePrepQuestions)
      .pipe(map(res => res));
  }

  getJourneyById(journey_id: number): Observable<JourneyDetailsResponse> {
    return this._coachHttpService
      .getJourneyById(journey_id,)
      .pipe(map(res => res));
  }

  getFilePath(journeyId: number, fileId: number): string {
    return this._coachHttpService.getFilePath(journeyId, fileId);
  }

}
